import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input, Modal, Table, Typography } from 'antd'
import { Icon } from '@iconify/react'
import { debounce } from 'lodash'
import dayjs from 'dayjs'

import {
    deleteSavedWorkloadReport,
    downloadSavedWorkloadPdf,
    listSavedWorkloadReports,
    updateSavedWorkloadReport,
} from './workload.service'
import { loginState } from '../../login/login.slice'
import { getReportsAccess } from '../../../helpers/reportsPermission'
import { showActionNotification } from '../../../components/notification'
import { PRIVATE_ROUTES } from '../../../routes'
import { useSelector } from 'react-redux'

import StoryMapDeleteConfirm from '../../projects/components/ProjectStoryMapBoard/components/StoryMapDeleteConfirm'
import styles from './SavedReportsPage.module.css'
import modalStyles from './components/SaveWorkloadReportModal.module.css'

const { Title } = Typography
const LIMIT = 20

const SavedReportsPage = () => {
    const navigate = useNavigate()
    const { userProfile } = useSelector(loginState)
    const reportsAccess = useMemo(
        () => getReportsAccess(userProfile),
        [userProfile]
    )

    const [rows, setRows] = useState([])
    const [search, setSearch] = useState('')
    const [skip, setSkip] = useState(0)
    const [isNext, setIsNext] = useState(false)
    const [canEdit, setCanEdit] = useState(false)
    const [status, setStatus] = useState('idle')
    const [busyId, setBusyId] = useState(null)
    const [renameTarget, setRenameTarget] = useState(null)
    const [renameValue, setRenameValue] = useState('')
    const [renaming, setRenaming] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState(null)

    const loadList = useCallback(
        async ({ reset = false, searchValue = search, nextSkip } = {}) => {
            setStatus(reset ? 'loading' : 'loadingMore')
            const requestSkip = reset ? 0 : nextSkip ?? skip
            try {
                const resp = await listSavedWorkloadReports({
                    skip: requestSkip,
                    limit: LIMIT,
                    search: searchValue || '',
                })
                const body = resp?.data ?? {}
                const data = Array.isArray(body.data) ? body.data : []
                setRows((prev) => (reset ? data : [...prev, ...data]))
                setSkip(requestSkip + data.length)
                setIsNext(Boolean(body.isNext))
                setCanEdit(Boolean(body.canEdit))
                setStatus('loaded')
            } catch (error) {
                setStatus('error')
                showActionNotification({
                    type: 'error',
                    message:
                        error?.response?.data?.message ||
                        error?.message ||
                        'Failed to load saved reports',
                })
            }
        },
        [search, skip]
    )

    useEffect(() => {
        loadList({ reset: true, searchValue: '' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                setSkip(0)
                loadList({ reset: true, searchValue: value })
            }, 350),
        [loadList]
    )

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const handleDownload = async (record) => {
        if (!record?._id || busyId) return
        setBusyId(record._id)
        const result = await downloadSavedWorkloadPdf(record._id)
        setBusyId(null)
        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to download PDF',
            })
        }
    }

    const handleDelete = (record) => {
        if (!record?._id || !canEdit) return
        setDeleteTarget(record)
    }

    const handleDeleteConfirm = async () => {
        if (!deleteTarget?._id) return
        const result = await deleteSavedWorkloadReport(deleteTarget._id)
        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to delete report',
            })
            return
        }
        setRows((prev) => prev.filter((r) => r._id !== deleteTarget._id))
        setDeleteTarget(null)
        showActionNotification({
            type: 'success',
            message: result.message || 'Report deleted',
        })
    }

    const handleRenameSubmit = async () => {
        const nextName = renameValue.trim()
        if (!renameTarget?._id || !nextName || renaming) return
        setRenaming(true)
        const result = await updateSavedWorkloadReport(renameTarget._id, {
            name: nextName,
        })
        setRenaming(false)
        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to rename report',
            })
            return
        }
        setRows((prev) =>
            prev.map((r) =>
                r._id === renameTarget._id ? { ...r, name: nextName } : r
            )
        )
        setRenameTarget(null)
        showActionNotification({
            type: 'success',
            message: result.message || 'Report updated',
        })
    }

    const columns = [
        {
            title: 'Report',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <button
                    type="button"
                    className={styles.nameBtn}
                    onClick={() =>
                        navigate(
                            PRIVATE_ROUTES.reportingWorkloadSavedDetail(
                                record._id
                            )
                        )
                    }
                >
                    {name || 'Untitled report'}
                </button>
            ),
        },
        {
            title: 'Range',
            key: 'range',
            width: 220,
            render: (_, record) => {
                const start = record?.filters?.start
                const end = record?.filters?.end
                if (!start || !end) return '—'
                const a = dayjs(start).format('D MMM YYYY')
                const b = dayjs(end).format('D MMM YYYY')
                return a === b ? a : `${a} – ${b}`
            },
        },
        {
            title: 'Visibility',
            dataIndex: 'visibility',
            key: 'visibility',
            width: 120,
            render: (v) => (
                <span className={styles.visibilityChip}>
                    {v === 'private' ? 'Private' : 'Workspace'}
                </span>
            ),
        },
        {
            title: 'Saved',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 190,
            render: (value) => (
                <span className={styles.savedDate}>
                    {value ? dayjs(value).format('D MMM YYYY, h:mm A') : '—'}
                </span>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 148,
            align: 'right',
            render: (_, record) => (
                <div className={styles.rowActions}>
                    {reportsAccess.canView ? (
                        <button
                            type="button"
                            className={styles.iconBtn}
                            title="Download PDF"
                            disabled={busyId === record._id}
                            onClick={() => handleDownload(record)}
                        >
                            <Icon
                                icon={
                                    busyId === record._id
                                        ? 'line-md:loading-loop'
                                        : 'mdi:download-outline'
                                }
                                width={16}
                            />
                        </button>
                    ) : null}
                    {canEdit ? (
                        <button
                            type="button"
                            className={styles.iconBtn}
                            title="Rename"
                            onClick={() => {
                                setRenameTarget(record)
                                setRenameValue(record?.name || '')
                            }}
                        >
                            <Icon icon="mdi:pencil-outline" width={16} />
                        </button>
                    ) : null}
                    {canEdit ? (
                        <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                            title="Delete"
                            onClick={() => handleDelete(record)}
                        >
                            <Icon icon="mdi:trash-can-outline" width={16} />
                        </button>
                    ) : null}
                </div>
            ),
        },
    ]

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button
                    type="button"
                    className={styles.backLink}
                    onClick={() => navigate(PRIVATE_ROUTES.reportingWorkload)}
                >
                    <Icon icon="mdi:arrow-left" width={18} height={18} />
                    Workload
                </button>
                <div className={styles.headerTop}>
                    <div>
                        <Title level={3} className={styles.title}>
                            Saved reports
                        </Title>
                        <p className={styles.subtitle}>
                            Frozen workload snapshots from previously saved
                            filters
                        </p>
                    </div>
                    <div className={styles.searchWrap}>
                        <Input
                            allowClear
                            placeholder="Search saved reports"
                            value={search}
                            prefix={
                                <Icon icon="mdi:magnify" width={18} height={18} />
                            }
                            onChange={(e) => {
                                const value = e.target.value
                                setSearch(value)
                                debouncedSearch(value)
                            }}
                        />
                    </div>
                </div>
            </header>

            <div className={styles.content}>
                {status === 'loading' && rows.length === 0 ? (
                    <div className={styles.centered}>
                        <Icon
                            icon="line-md:loading-loop"
                            style={{ fontSize: 40, color: '#6366f1' }}
                        />
                    </div>
                ) : (
                    <div className={styles.tableWrap}>
                        <Table
                            rowKey="_id"
                            columns={columns}
                            dataSource={rows}
                            pagination={false}
                            locale={{
                                emptyText: (
                                    <div className={styles.empty}>
                                        <Icon
                                            icon="mdi:file-document-outline"
                                            width={28}
                                        />
                                        <p>No saved reports yet</p>
                                    </div>
                                ),
                            }}
                        />
                        {isNext ? (
                            <div className={styles.loadMore}>
                                <button
                                    type="button"
                                    className={styles.loadMoreBtn}
                                    disabled={status === 'loadingMore'}
                                    onClick={() => loadList({ reset: false, nextSkip: skip })}
                                >
                                    {status === 'loadingMore'
                                        ? 'Loading…'
                                        : 'Load more'}
                                </button>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>

            <StoryMapDeleteConfirm
                open={Boolean(deleteTarget)}
                title="Delete report?"
                message={`"${deleteTarget?.name || 'This report'}" will be removed from Saved reports.`}
                centerActions
                onCancel={() => setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
            />

            <Modal
                className={modalStyles.modal}
                title={
                    <div className={modalStyles.header}>
                        <h2 className={modalStyles.modalTitle}>
                            Rename report
                        </h2>
                    </div>
                }
                open={Boolean(renameTarget)}
                onCancel={() => !renaming && setRenameTarget(null)}
                footer={null}
                width={460}
                centered
                destroyOnHidden
                maskClosable={!renaming}
                closable={!renaming}
            >
                <div className={modalStyles.body}>
                    <div className={modalStyles.field}>
                        <label
                            className={modalStyles.label}
                            htmlFor="rename-report-name"
                        >
                            Report name
                        </label>
                        <Input
                            id="rename-report-name"
                            value={renameValue}
                            maxLength={200}
                            allowClear
                            autoFocus
                            placeholder="e.g. Workload · 11 Aug 2026"
                            disabled={renaming}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onPressEnter={handleRenameSubmit}
                        />
                    </div>

                    <div className={modalStyles.modalFooter}>
                        <div className={modalStyles.formActions}>
                            <button
                                type="button"
                                onClick={() =>
                                    !renaming && setRenameTarget(null)
                                }
                                disabled={renaming}
                                className={modalStyles.cancelButton}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={
                                    renaming ||
                                    !renameValue.trim() ||
                                    renameValue.trim().length > 200
                                }
                                onClick={handleRenameSubmit}
                                className={`${modalStyles.submitButton} ${
                                    renaming
                                        ? modalStyles.submitButtonDisabled
                                        : ''
                                }`}
                            >
                                {renaming ? (
                                    <>
                                        <Icon
                                            icon="line-md:loading-loop"
                                            width={16}
                                            height={16}
                                        />
                                        Saving…
                                    </>
                                ) : (
                                    'Save'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default SavedReportsPage
